import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useSupplierNotifications } from "@/hooks/useSupplierNotifications";

interface SupplierNotificationsDropdownProps {
  supplierId: string;
}

export const SupplierNotificationsDropdown = ({ supplierId }: SupplierNotificationsDropdownProps) => {
  const { data, isLoading } = useSupplierNotifications(supplierId);
  
  if (isLoading || !data) {
    return null;
  }

  const { totalCount, newMatchesCount, newMessagesCount, newConversationsCount, bookingConfirmedCount } = data;
  const hasNotifications = totalCount > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="relative p-2 hover:bg-gray-100 rounded-full transition-colors">
        <Bell className="w-5 h-5 text-gray-700" />
        {totalCount > 0 && (
          <Badge 
            variant="destructive" 
            className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-1 text-xs"
          >
            {totalCount > 99 ? '99+' : totalCount}
          </Badge>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-80 bg-white border border-gray-100/50 shadow-lg rounded-lg z-50 p-2 mt-2"
        align="end"
      >
        <div className="px-3 py-2 font-semibold text-sm border-b">
          Notiser
        </div>
        
        {!hasNotifications ? (
          <div className="px-3 py-4 text-sm text-gray-500 text-center">
            Inga nya notiser
          </div>
        ) : (
          <div className="py-1">
            {newMatchesCount > 0 && (
              <div className="flex items-center gap-3 px-3 py-3">
                <span className="text-xl">🎯</span>
                <span className="text-sm text-dark-blue">
                  {newMatchesCount} {newMatchesCount === 1 ? 'ny förfrågan' : 'nya förfrågningar'}
                </span>
              </div>
            )}
            {newConversationsCount > 0 && (
              <div className="flex items-center gap-3 px-3 py-3">
                <span className="text-xl">📩</span>
                <span className="text-sm text-dark-blue">
                  {newConversationsCount} {newConversationsCount === 1 ? 'ny konversation' : 'nya konversationer'}
                </span>
              </div>
            )}
            {newMessagesCount > 0 && (
              <div className="flex items-center gap-3 px-3 py-3">
                <span className="text-xl">💬</span>
                <span className="text-sm text-dark-blue">
                  {newMessagesCount} {newMessagesCount === 1 ? 'nytt meddelande' : 'nya meddelanden'}
                </span>
              </div>
            )}
            {bookingConfirmedCount > 0 && (
              <div className="flex items-center gap-3 px-3 py-3">
                <span className="text-xl">✅</span>
                <span className="text-sm text-dark-blue">
                  {bookingConfirmedCount} {bookingConfirmedCount === 1 ? 'kund har konfirmerat' : 'kunder har konfirmerat'}
                </span>
              </div>
            )}
          </div>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link 
            to="/supplier-conversations"
            className="w-full text-center px-3 py-2 text-sm text-blue-600 hover:bg-gray-50 rounded cursor-pointer"
          >
            Visa alla förfrågningar
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
